import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { TopShopClosedMessage } from "@/components/topshop/TopShopRegistrationClosed";
import { CLOSED_MESSAGE_MAX, formatJamaicaTime, isRegistrationOpen, isoToJamaicaLocal, jamaicaLocalToIso } from "@/components/topshop/topShopRegistration";
import { useTopShopRegistration } from "@/components/topshop/useTopShopRegistration";

export function TopShopRegistrationTab() {
  const queryClient = useQueryClient();
  const { program, isOpen, isLoading } = useTopShopRegistration();
  const [open, setOpen] = useState(true);
  const [closeLocal, setCloseLocal] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!program) return;
    setOpen(program.applications_open);
    setCloseLocal(isoToJamaicaLocal(program.applications_close_at));
    setMessage(program.applications_closed_message ?? "");
  }, [program]);

  if (isLoading || !program) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const savedClose = program.applications_close_at;
  const status = !program.applications_open
    ? "Closed (turned off)"
    : isOpen
      ? savedClose ? `Open until ${formatJamaicaTime(savedClose)}` : "Open"
      : `Closed since ${formatJamaicaTime(savedClose!)}`;

  const save = async () => {
    const closeAt = closeLocal ? jamaicaLocalToIso(closeLocal) : null;
    if (closeLocal && !closeAt) { toast.error("Please enter a valid date and time"); return; }
    if (message.length > CLOSED_MESSAGE_MAX) { toast.error(`Message must be ${CLOSED_MESSAGE_MAX} characters or fewer`); return; }
    setSaving(true);
    const { error } = await supabase.from("programs").update({
      applications_open: open,
      applications_close_at: closeAt,
      applications_closed_message: message.trim() ? message : null,
    }).eq("id", program.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Registration settings saved");
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["top-shop"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "top-shop"] }),
    ]);
  };

  const previewOpen = isRegistrationOpen(open, closeLocal ? jamaicaLocalToIso(closeLocal) : null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registration</CardTitle>
        <CardDescription>Status: <strong className="text-foreground">{status}</strong></CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-3">
          <Switch id="ts-accept" checked={open} onCheckedChange={setOpen} />
          <Label htmlFor="ts-accept">Accept applications</Label>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ts-close-at">Close registration at (Jamaica time)</Label>
          <div className="flex flex-wrap gap-2">
            <Input id="ts-close-at" type="datetime-local" className="w-auto" value={closeLocal} onChange={(e) => setCloseLocal(e.target.value)} />
            <Button type="button" variant="outline" onClick={() => setCloseLocal("")} disabled={!closeLocal}>Clear date</Button>
          </div>
          {!previewOpen && open && closeLocal && <p className="text-sm text-muted-foreground">This time has already passed, so registration will be closed once saved.</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="ts-closed-message">Message shown when registration is closed</Label>
          <Textarea id="ts-closed-message" rows={6} maxLength={CLOSED_MESSAGE_MAX} value={message} onChange={(e) => setMessage(e.target.value)} />
          <p className="text-xs text-muted-foreground">{message.length} / {CLOSED_MESSAGE_MAX}</p>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">Preview</p>
          <div className="rounded-md border bg-background p-5">
            <h2 className="text-2xl font-bold">Registration is closed</h2>
            <div className="mt-3"><TopShopClosedMessage message={message} /></div>
          </div>
        </div>
        <Button onClick={save} disabled={saving}>{saving && <Loader2 className="animate-spin" aria-hidden="true" />}Save</Button>
      </CardContent>
    </Card>
  );
}
